import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook to detect when sticky headers are stuck
 * Uses Intersection Observer to track when sentinel elements go out of view
 *
 * @returns {
 *   stuckHeaders: Set of header IDs that are currently stuck
 *   sentinelRefs: Map to store sentinel element references
 *   headerRefs: Map to store header element references (optional)
 * }
 */
export function useStickyHeaderDetection() {
  const [stuckHeaders, setStuckHeaders] = useState<Set<string>>(new Set());
  const sentinelRefs = useRef<Map<string, HTMLElement>>(new Map());
  const headerRefs = useRef<Map<string, HTMLElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      entries => {
        setStuckHeaders(prev => {
          const newSet = new Set(prev);
          entries.forEach(entry => {
            const headerId = entry.target.getAttribute('data-header-id');
            if (headerId) {
              if (!entry.isIntersecting) {
                // Sentinel is out of view, header is stuck
                newSet.add(headerId);
              } else {
                // Sentinel is in view, header is not stuck
                newSet.delete(headerId);
              }
            }
          });
          return newSet;
        });
      },
      {
        threshold: [1],
        rootMargin: '-1px 0px 0px 0px', // Trigger when sentinel is 1px out of view
      },
    );

    // Observe all current sentinels
    const observer = observerRef.current;
    sentinelRefs.current.forEach(sentinel => {
      observer.observe(sentinel);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []); // Empty deps, observer is stable

  // Function to register a new sentinel
  const registerSentinel = (id: string, element: HTMLElement | null) => {
    if (element) {
      sentinelRefs.current.set(id, element);
      // If observer exists, start observing immediately
      if (observerRef.current) {
        observerRef.current.observe(element);
      }
    } else {
      // Cleanup if element is removed
      const existing = sentinelRefs.current.get(id);
      if (existing && observerRef.current) {
        observerRef.current.unobserve(existing);
      }
      sentinelRefs.current.delete(id);
    }
  };

  // Function to register a header (optional, for future use)
  const registerHeader = (id: string, element: HTMLElement | null) => {
    if (element) {
      headerRefs.current.set(id, element);
    } else {
      headerRefs.current.delete(id);
    }
  };

  return {
    stuckHeaders,
    registerSentinel,
    registerHeader,
    isStuck: (id: string) => stuckHeaders.has(id),
  };
}
